import { api } from './client'
import type {
  Attendance,
  Bootstrap,
  BankAccount,
  CalendarResponse,
  Center,
  CashReceipt,
  CashReceiptPreview,
  Client,
  ClientFee,
  ChangeEntry,
  ChangeKind,
  Codebook,
  ClientReservation,
  MissingTariff,
  ReservationQuote,
  RewardBlockQuote,
  RewardHistoryRow,
  RewardLessonQuote,
  ClientReservationRow,
  DuplicateGroup,
  EmailSummary,
  FeeType,
  FioImportResult,
  Instructor,
  InstructorReservation,
  InstructorRateRow,
  InstructorReward,
  InstructorPortal,
  MergeResult,
  InvoiceCustomer,
  InvoiceDraft,
  OrderDetail,
  OrderInvoice,
  OutgoingEmail,
  OutgoingEmailDetail,
  Paginated,
  Payment,
  PaymentListRow,
  Payslip,
  PricingRow,
  UnmatchedPaymentRow,
  RewardType,
  RewardsSummary,
  SchoolDocuments,
  TenantBilling,
  TermsAcceptance,
  TermsDocument,
  Settings,
  StatementImportResult,
  StatisticsResponse,
  User,
  WidgetConfig,
} from '@/types/api'

/** Dvojfaktorové overenie vlastného účtu (`apps.accounts.mfa`). */
export interface MfaState {
  enabled: boolean
  enabled_at: string | null
  recovery_left: number
  /** Len pri `setup`: tajomstvo, odkaz pre aplikáciu a QR kód (SVG data URI). */
  secret?: string
  uri?: string
  qr?: string
  /** Len pri `enable` a `recovery` - ukážu sa raz. */
  recovery_codes?: string[]
}

/** Kontrolný zoznam pre novú školu (`apps.core.onboarding`). */
export const onboarding = {
  get: () =>
    api
      .get<{
        show: boolean
        steps: Array<{ key: string; done: boolean; link: string }>
        done: number
        total: number
      }>('/onboarding')
      .then((r) => r.data),
}

// --- auth -------------------------------------------------------------------
export const auth = {
  /** So zapnutým MFA vráti namiesto tokenov `mfa_required` a výzvu na kód. */
  login: (email: string, password: string, remember = false) =>
    api
      .post<
        | { user: User; access: string; mfa_required?: undefined }
        | { mfa_required: true; challenge: string }
      >('/auth/login', { email, password, remember })
      .then((r) => r.data),
  loginMfa: (challenge: string, code: string) =>
    api
      .post<{ user: User; access: string }>('/auth/login/mfa', { challenge, code })
      .then((r) => r.data),
  logout: () => api.post('/auth/logout').then((r) => r.data),
  me: () => api.get<User>('/auth/me').then((r) => r.data),
  /** Logo školy pre prihlásenie - bez prihlásenia (`/api/branding`). */
  branding: () =>
    api
      .get<{ logo_url: string; system_name: string; favicon_url: string }>('/branding')
      .then((r) => r.data),
  updateMe: (payload: Partial<User>) => api.patch<User>('/auth/me', payload).then((r) => r.data),
  changePassword: (old_password: string, new_password: string) =>
    api.post('/auth/change-password', { old_password, new_password }).then((r) => r.data),
  mfa: () => api.get<MfaState>('/auth/mfa').then((r) => r.data),
  mfaAction: (
    action: 'setup' | 'enable' | 'disable' | 'recovery',
    payload: { code?: string; password?: string } = {},
  ) => api.post<MfaState>('/auth/mfa', { action, ...payload }).then((r) => r.data),
  forgotPassword: (email: string) =>
    api.post('/auth/forgot-password', { email }).then((r) => r.data),
  resetPassword: (token: string, new_password: string) =>
    api.post('/auth/reset-password', { token, new_password }).then((r) => r.data),
}

export const bootstrap = () => api.get<Bootstrap>('/bootstrap').then((r) => r.data)

// --- calendar ---------------------------------------------------------------
export const calendar = {
  /** `centers` - len vybrané strediská (`sql/040`); prázdne = všetky. */
  get: (start?: string, days = 7, centers: number[] = []) =>
    api
      .get<CalendarResponse>('/calendar', {
        params: { start, days, centers: centers.length ? centers.join(',') : undefined },
      })
      .then((r) => r.data),
  /** Token, ktorý sa zmení pri každej zmene rezervácie — pozri `useCalendarSync`. */
  version: () =>
    api.get<{ version: string }>('/calendar/version').then((r) => r.data.version),
}

export interface InstructorReservationPayload {
  id?: number | null
  instructor_id?: number | null
  fname: string
  sname?: string | null
  email?: string | null
  tel?: string | null
  start: string
  end: string
  status_id: number
  language_ids: number[]
  course_ids: number[]
  owncar: boolean
  passengers_in_car: number
  note?: string | null
  /** Stredisko bloku (`sql/040`); lekcie bloku ho preberajú. */
  center_id?: number
}

export const instructorReservations = {
  create: (payload: InstructorReservationPayload) =>
    api.post<InstructorReservation>('/instructor-reservations/', payload).then((r) => r.data),
  /** Jeden inštruktor, viac termínov. Všetko alebo nič — viď `bulk` na serveri. */
  createMany: (payload: InstructorReservationPayload & { occurrences: Occurrence[] }) =>
    api
      .post<InstructorReservation[]>('/instructor-reservations/bulk/', payload)
      .then((r) => r.data),
  update: (id: number, payload: InstructorReservationPayload) =>
    api.put<InstructorReservation>(`/instructor-reservations/${id}/`, payload).then((r) => r.data),
  remove: (id: number) => api.delete(`/instructor-reservations/${id}/`),
  setStatus: (id: number, status_id: number) =>
    api
      .post<InstructorReservation>(`/instructor-reservations/${id}/status/`, { status_id })
      .then((r) => r.data),
  /** Fills every day of the visible window, not just the first column. */
  createForFavorites: (day: string, days = 1) =>
    api
      .post<InstructorReservation[]>('/instructor-reservations/favorites/', { day, days })
      .then((r) => r.data),
  pdfUrl: (id: number) => `/api/instructor-reservations/${id}/pdf/`,
  dayPdfUrl: (day: string) => `/api/instructor-reservations/day-pdf/?day=${day}`,
}

export interface ClientReservationPayload {
  id?: number | null
  client_id?: number | null
  instructor_reservation_id?: number | null
  fname?: string | null
  sname: string
  email?: string | null
  tel?: string | null
  client_note?: string | null
  is_children: boolean
  /** Úroveň klienta; `is_beginner` si z nej server odvodí. */
  levelid: number | null
  start: string
  end: string
  status_id: number
  lang_id: number
  course_id: number
  numbers: number
  /** Nepovinné - bez neho server stav platby nemení (úhrada sa zapisuje
   *  v detailoch platby). */
  client_paid?: number
  note?: string | null
  /** Kurz objednaný cez školu, nie bežná rezervácia klienta. */
  is_school_course?: boolean
  /** Používateľ potvrdil zmenu uhradenej rezervácie - bez toho ju server odmietne. */
  confirm_paid?: boolean
  /** Vlastné bonusy inštruktora (`sql/038`). */
  bonus_ids?: number[]
  /** Stredisko lekcie bez inštruktora (`sql/040`). */
  center_id?: number
  /** Účet, na ktorý má klient platiť (`sql/042`); null = predvolený. */
  bank_account_id?: number | null
}

/** Ďalší termín tej istej rezervácie — kurz rozložený do viacerých dní. */
export interface Occurrence {
  start: string
  end: string
}

export const clientReservations = {
  /** Zapnúť / vypnúť vlastné bonusy inštruktora pri lekcii (odmena sa hneď
   *  prepočíta). */
  setBonuses: (id: number, bonusIds: number[]) =>
    api
      .post<{ bonus_ids: number[] }>(`/client-reservations/${id}/bonuses/`, { bonus_ids: bonusIds })
      .then((r) => r.data),
  /** Zoznam so stránkovaním — filtre a zoradenie sú v `params`. */
  list: (params: Record<string, unknown>) =>
    api
      .get<Paginated<ClientReservationRow>>('/client-reservations/', { params })
      .then((r) => r.data),
  create: (payload: ClientReservationPayload) =>
    api.post<ClientReservation>('/client-reservations/', payload).then((r) => r.data),
  /** Jeden klient, viac termínov. Všetko alebo nič — viď `bulk` na serveri. */
  createMany: (payload: ClientReservationPayload & { occurrences: Occurrence[] }) =>
    api
      .post<ClientReservation[]>('/client-reservations/bulk/', payload)
      .then((r) => r.data),
  update: (id: number, payload: ClientReservationPayload) =>
    api.put<ClientReservation>(`/client-reservations/${id}/`, payload).then((r) => r.data),
  remove: (id: number) => api.delete(`/client-reservations/${id}/`),
  setStatus: (id: number, status_id: number) =>
    api
      .post<ClientReservation>(`/client-reservations/${id}/status/`, { status_id })
      .then((r) => r.data),
  setPayment: (id: number, client_paid: number) =>
    api
      .post<ClientReservation>(`/client-reservations/${id}/payment/`, { client_paid })
      .then((r) => r.data),
  move: (
    id: number,
    body: {
      day: string
      part: number
      instructor_reservation_id?: number | null
      copy?: boolean
      confirm_paid?: boolean
    },
  ) => api.post<ClientReservation>(`/client-reservations/${id}/move/`, body).then((r) => r.data),
  history: (id: number) =>
    api.get<Array<Record<string, unknown>>>(`/client-reservations/${id}/history/`).then((r) => r.data),
  /** O koľko by sa zmenila cena lekcie s týmito údajmi - nič neukladá. */
  pricePreview: (
    id: number,
    body: { start: string; end: string; numbers: number; lang_id: number; status_id: number },
  ) =>
    api
      .post<{ delta: string }>(`/client-reservations/${id}/price-preview/`, body)
      .then((r) => r.data),
  /** Orientačná cena neuloženej rezervácie - nič sa nezapisuje. */
  quote: (body: {
    terms: Array<{ start: string; end: string }>
    numbers: number
    lang_id: number
    status_id: number
    /** Cenník strediska (`sql/040`). */
    center_id?: number
  }) => api.post<ReservationQuote>('/client-reservations/quote/', body).then((r) => r.data),
}

// --- clients ----------------------------------------------------------------
export const clients = {
  list: (params: Record<string, unknown>) =>
    api.get<Paginated<Client>>('/clients/', { params }).then((r) => r.data),
  get: (id: number) => api.get<Client>(`/clients/${id}/`).then((r) => r.data),
  /** Útrata (bez zrušených), zaplatené a nedoplatok - zo skutočných platieb. */
  money: (id: number) =>
    api
      .get<{ charged: string; paid: string; due: string }>(`/clients/${id}/money/`)
      .then((r) => r.data),
  /** Kredit zákazníka - zostatok a pohyby. */
  credit: (id: number) =>
    api
      .get<{
        balance: string
        movements: Array<{ id: number; created: string; amount: string; ref: number | null; note: string }>
      }>(`/clients/${id}/credit/`)
      .then((r) => r.data),
  create: (payload: Partial<Client>) => api.post<Client>('/clients/', payload).then((r) => r.data),
  update: (id: number, payload: Partial<Client>) =>
    api.patch<Client>(`/clients/${id}/`, payload).then((r) => r.data),
  remove: (id: number) => api.delete(`/clients/${id}/`),
  reservations: (id: number, page?: number, limit?: number) =>
    api
      .get<Paginated<ClientReservation> | ClientReservation[]>(`/clients/${id}/reservations/`, {
        params: { page, limit },
      })
      .then((r) => r.data),
  fees: (id: number) => api.get<ClientFee[]>(`/clients/${id}/fees/`).then((r) => r.data),
  duplicates: () => api.get<DuplicateGroup[]>('/clients/duplicates/').then((r) => r.data),
  merge: (target_id: number, source_ids: number[]) =>
    api
      .post<MergeResult>('/clients/merge/', { target_id, source_ids })
      .then((r) => r.data),
  sales: (from?: string, to?: string) =>
    api.get('/clients/sales/', { params: { from, to } }).then((r) => r.data),
  /** Rovnaký filter (`q`), aký práve vidno na obrazovke - nie len jedna
   *  stránka, celý zoznam vyhovujúci hľadaniu. */
  exportUrl: (filetype: 'xlsx' | 'csv', q?: string) =>
    api
      .get('/clients/export/', { params: { filetype, q }, responseType: 'blob' })
      .then((r) => ({
        blob: r.data as Blob,
        filename:
          /filename="([^"]+)"/.exec(r.headers['content-disposition'] ?? '')?.[1] ??
          `klienti.${filetype}`,
      })),
}

// --- instructors ------------------------------------------------------------
export const instructors = {
  list: (params: Record<string, unknown>) =>
    api.get<Paginated<Instructor>>('/instructors/', { params }).then((r) => r.data),
  get: (id: number) => api.get<Instructor>(`/instructors/${id}/`).then((r) => r.data),
  create: (payload: Partial<Instructor>) =>
    api.post<Instructor>('/instructors/', payload).then((r) => r.data),
  update: (id: number, payload: Partial<Instructor>) =>
    api.patch<Instructor>(`/instructors/${id}/`, payload).then((r) => r.data),
  remove: (id: number, force = false) =>
    api.delete(`/instructors/${id}/`, { params: force ? { force: 1 } : {} }),
  restore: (id: number) =>
    api.post<Instructor>(`/instructors/${id}/restore/`).then((r) => r.data),
  toggleFavorite: (id: number) =>
    api.post<Instructor>(`/instructors/${id}/toggle-favorite/`).then((r) => r.data),
  /** Zrušiť prepojenie s Messengerom — token prestane platiť. */
  revokeMessenger: (id: number) =>
    api
      .post<Instructor>(`/instructors/${id}/messenger/`, { revoke: true })
      .then((r) => r.data),
  setPriority: (order: number[]) => api.post('/instructors/priority/', { order }).then((r) => r.data),
  rewards: (
    id: number,
    from?: string,
    to?: string,
    page?: number,
    pageSize?: number,
    taughtOnly = false,
    centers: number[] = [],
  ) =>
    api
      .get<{
        instructor: Instructor
        from: string
        to: string
        items: InstructorReward[]
        /** Total rows in the period - `items` is only the current page. */
        count: number
        page: number
        page_size: number
        pages: number
        /** Always for the whole period, never just the page. */
        summary: RewardsSummary
        lessons: { lessons: Record<string, number>; total: number }
      }>(`/instructors/${id}/rewards/`, {
        params: {
          from,
          to,
          page,
          page_size: pageSize,
          taught_only: taughtOnly ? 1 : undefined,
          centers: centers.length ? centers.join(',') : undefined,
        },
      })
      .then((r) => r.data),
  /** Tajný odkaz na mobilný pohľad inštruktora; `renew` vydá nový. */
  portal: (id: number, renew = false) =>
    (renew
      ? api.post<{ path: string }>(`/instructors/${id}/portal/`)
      : api.get<{ path: string }>(`/instructors/${id}/portal/`)
    ).then((r) => r.data),
  rewardsOverview: (
    from?: string,
    to?: string,
    instructorIds: number[] = [],
    taughtOnly = false,
    centers: number[] = [],
  ) =>
    api
      .get<{ from: string; to: string; rows: Array<{ instructor: Instructor; summary: RewardsSummary }> }>(
        '/instructors/rewards/',
        {
          params: {
            from,
            to,
            instructor_ids: instructorIds.length ? instructorIds.join(',') : undefined,
            taught_only: taughtOnly ? 1 : undefined,
            centers: centers.length ? centers.join(',') : undefined,
          },
        },
      )
      .then((r) => r.data),
}

// --- money ------------------------------------------------------------------
export const fees = {
  list: (params: Record<string, unknown>) =>
    api.get<Paginated<ClientFee>>('/fees/', { params }).then((r) => r.data),
  addCustom: (body: {
    client_reservation_id: number
    amount: string | number
    description?: string
    type_id?: number | null
  }) => api.post<ClientFee>('/fees/custom/', body).then((r) => r.data),
  update: (
    id: number,
    payload: {
      description?: string
      amount?: string | number
      discount_type?: 'percent' | 'amount' | null
      discount_value?: string | number | null
    },
  ) => api.patch<ClientFee>(`/fees/${id}/`, payload).then((r) => r.data),
  setTotalDiscount: (body: {
    client_reservation_id: number
    discount_type?: 'percent' | 'amount' | null
    discount_value?: string | number | null
  }) => api.post('/fees/total-discount/', body).then((r) => r.data),
  remove: (id: number) => api.delete(`/fees/${id}/`),
  markPaid: (ids: number[], paid = true) =>
    api.post('/fees/mark-paid/', { ids, paid }).then((r) => r.data),
  recalculate: (body: { client_reservation_id?: number; from?: string; to?: string }) =>
    api.post('/fees/recalculate/', body).then((r) => r.data),
  types: () => api.get<FeeType[]>('/fee-types/').then((r) => r.data),
  saveType: (id: number, payload: Partial<FeeType>) =>
    api.patch<FeeType>(`/fee-types/${id}/`, payload).then((r) => r.data),
}

export const rewards = {
  list: (params: Record<string, unknown>) =>
    api.get<Paginated<InstructorReward>>('/rewards/', { params }).then((r) => r.data),
  update: (id: number, payload: { reward?: string | number; description?: string }) =>
    api.patch<InstructorReward>(`/rewards/${id}/`, payload).then((r) => r.data),
  markPaid: (ids: number[], paid = true) =>
    api.post('/rewards/mark-paid/', { ids, paid }).then((r) => r.data),
  /** Hand-written reward line for one lesson (reward type 999). */
  addManual: (body: {
    client_reservation_id: number
    reward: string | number
    description?: string
  }) => api.post<InstructorReward>('/rewards/manual/', body).then((r) => r.data),
  remove: (id: number) => api.delete(`/rewards/${id}/`),
  recalculate: (body: { instructor_reservation_id?: number; from?: string; to?: string }) =>
    api.post('/rewards/recalculate/', body).then((r) => r.data),
  /** Odmena za neuložené lekcie (formulár rezervácie) - nič sa nezapisuje. */
  quoteLessons: (body: {
    instructor_reservation_id: number | null
    terms: Array<{ start: string; end: string }>
    numbers: number
    lang_id: number
    status_id: number
    bonus_ids?: number[]
  }) => api.post<RewardLessonQuote>('/rewards/quote-lessons/', body).then((r) => r.data),
  /** Odmena za blok pred uložením (auto, odvoz, lekcie v bloku). */
  quoteBlock: (body: {
    id: number | null
    instructor_id: number | null
    start: string
    end: string
    status_id: number
    owncar: boolean
    passengers_in_car: number
  }) => api.post<RewardBlockQuote>('/rewards/quote-block/', body).then((r) => r.data),
  /** Ako sa výška odmeny menila v čase - lekcia a jej blok (celý deň). */
  history: (params: { client_reservation_id?: number; instructor_reservation_id?: number | null }) =>
    api.get<RewardHistoryRow[]>('/rewards/history/', { params }).then((r) => r.data),
  types: () => api.get<RewardType[]>('/reward-types/').then((r) => r.data),
  saveType: (id: number, payload: Partial<RewardType>) =>
    api.patch<RewardType>(`/reward-types/${id}/`, payload).then((r) => r.data),
  /** Nový vlastný bonus (vstavané typy zakladá len SQL). */
  createBonus: (payload: Pick<RewardType, 'name' | 'reward' | 'per'> & { allowed?: boolean }) =>
    api.post<RewardType>('/reward-types/', payload).then((r) => r.data),
  deleteType: (id: number) => api.delete(`/reward-types/${id}/`),
}

/**
 * Objednávka = všetko, čo visí na jednom variabilnom symbole.
 *
 * Zákazník platí kurz jednou sumou, takže jednotkou platby nie je lekcia:
 * zapísaná úhrada sa prejaví na všetkých lekciách toho istého symbolu.
 */
export const orders = {
  get: (ref: number) => api.get<OrderDetail>(`/orders/${ref}`).then((r) => r.data),
  /** Bez `amount` sa uhradí celý zostatok; so sumou je to čiastočná úhrada. */
  pay: (
    ref: number,
    body: { amount?: string | number; payment_type: number; note?: string; ekasa_ref?: string },
  ) =>
    api.post<Payment>(`/orders/${ref}`, body).then((r) => r.data),
  removePayment: (id: number) => api.delete(`/payments/${id}`),
  /** Podklady faktúry k objednávke (odberateľ, položky, DPH). */
  invoiceDraft: (ref: number) => api.get<InvoiceDraft>(`/orders/${ref}/invoice`).then((r) => r.data),
  issueInvoice: (
    ref: number,
    body: InvoiceCustomer & { issue_date?: string; supply_date?: string; note?: string },
  ) => api.post<OrderInvoice>(`/orders/${ref}/invoice`, body).then((r) => r.data),
  cancelInvoice: (id: number) => api.post<OrderInvoice>(`/client-invoices/${id}/storno`).then((r) => r.data),
  invoicePdfUrl: (id: number, storno = false) => `/api/client-invoices/${id}/pdf${storno ? '?storno=1' : ''}`,
  /** Poslať klientovi doklady objednávky, ktoré ešte neodišli. */
  sendDocuments: (ref: number) =>
    api.post<{ sent: number }>(`/orders/${ref}/send-documents`).then((r) => r.data),
  /** Doplniť UID / číslo dokladu z eKasy k platbe. */
  setEkasaRef: (id: number, ekasa_ref: string) =>
    api.patch<Payment>(`/payments/${id}`, { ekasa_ref }).then((r) => r.data),
  /** Vrátiť peniaze - peniazmi (`payment_type`), alebo na kredit zákazníka. */
  refund: (
    ref: number,
    body: { amount: string; mode: 'money' | 'credit'; payment_type?: number; note?: string },
  ) =>
    api
      .post<{ payment: Payment | null; storno_receipts: number[] }>(`/orders/${ref}/refund`, body)
      .then((r) => r.data),
  /** Uhradiť objednávku z kreditu zákazníka. */
  payFromCredit: (ref: number) =>
    api.post<Payment>(`/orders/${ref}/credit`).then((r) => r.data),
}

/**
 * Zoznam platieb naprieč celou školou — na čítanie. Zápis (`pay`) a mazanie
 * (`removePayment`) jednej platby je vždy cez `orders`, ktorý vyžaduje
 * existujúci zostatok na konkrétnej objednávke.
 */
export const payments = {
  list: (params: Record<string, unknown>) =>
    api.get<Paginated<PaymentListRow>>('/payments', { params }).then((r) => r.data),
}

/**
 * Front na ručné spárovanie platieb z Fio importu, ktoré sa nepodarilo
 * priradiť automaticky (zlý symbol, alebo objednávka, ktorá už je vybavená).
 */
export const unmatchedPayments = {
  list: (params: Record<string, unknown>) =>
    api.get<Paginated<UnmatchedPaymentRow>>('/payments/unmatched', { params }).then((r) => r.data),
  match: (id: number, body: { ref: number; payment_type: number; note?: string }) =>
    api.post<Payment>(`/payments/unmatched/${id}/match`, body).then((r) => r.data),
  dismiss: (id: number) => api.post(`/payments/unmatched/${id}/dismiss`),
}

export const cashReceipts = {
  list: (params: Record<string, unknown>) =>
    api.get<Paginated<CashReceipt>>('/cash-receipts/', { params }).then((r) => r.data),
  preview: (client_reservation_id: number) =>
    api
      .get<CashReceiptPreview>('/cash-receipts/preview/', { params: { client_reservation_id } })
      .then((r) => r.data),
  issue: (client_reservation_id: number, description?: string, payment_type?: number) =>
    api
      .post<CashReceipt>('/cash-receipts/issue/', {
        client_reservation_id,
        description,
        payment_type,
      })
      .then((r) => r.data),
  /**
   * Doklad na vybrané lekcie — cesta z okna platby.
   *
   * Bez `payment_type` sa spôsob úhrady odvodí z platieb, ktoré doklad
   * dokladuje; operátor by inak vyberal znova to, čo systém už vie.
   */
  issueFor: (
    client_reservation_ids: number[],
    description?: string,
    payment_type?: number,
  ) =>
    api
      .post<CashReceipt>('/cash-receipts/issue/', {
        client_reservation_ids,
        description,
        ...(payment_type === undefined ? {} : { payment_type }),
      })
      .then((r) => r.data),
  correct: (
    id: number,
    payload: {
      receipt_name?: string
      receipt_description?: string
      payment_type?: number
    },
  ) => api.patch<CashReceipt>(`/cash-receipts/${id}/correct/`, payload).then((r) => r.data),
  storno: (id: number, amount?: string | number) =>
    api.post<CashReceipt>(`/cash-receipts/${id}/storno/`, { amount }).then((r) => r.data),
  pdfUrl: (id: number, storno = false) =>
    `/api/cash-receipts/${id}/pdf/${storno ? '?storno=1' : ''}`,
}

/**
 * Jazyky, v ktorých škola učí.
 *
 * Zoznam vracia **všetky** aj s príznakom `use` — vypnutý jazyk zo systému
 * nemizne, staré lekcie ho majú zapísaný a musia si ho vedieť pomenovať.
 */
export const languages = {
  list: () => api.get<Codebook[]>('/languages/').then((r) => r.data),
  setUse: (id: number, use: boolean) =>
    api.patch<Codebook>(`/languages/${id}/`, { use }).then((r) => r.data),
}

// --- settings & admin -------------------------------------------------------
export const settings = {
  get: () => api.get<Settings>('/settings').then((r) => r.data),
  update: (payload: Partial<Settings>) =>
    api.patch<Settings>('/settings', payload).then((r) => r.data),
  uploadLogo: (file: File) => {
    const form = new FormData()
    form.append('logo', file)
    return api
      .post<Settings>('/settings/logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
  /** Cenník strediska (`sql/040`), predvolene 1. */
  pricing: (centerId = 1) =>
    api
      .get<{ rows: PricingRow[]; persons: number[]; minutes: number[]; langs: string[] }>(
        '/pricing',
        { params: { center_id: centerId } },
      )
      .then((r) => r.data),
  savePricing: (rows: PricingRow[], centerId = 1) =>
    api.put('/pricing', { rows, center_id: centerId }).then((r) => r.data),
  /** Skopírovať cenník strediska do iného (cieľový sa nahradí). */
  copyPricing: (fromCenterId: number, toCenterId: number) =>
    api
      .post<{ saved: number; repriced: number }>('/pricing/copy', {
        from_center_id: fromCenterId,
        to_center_id: toCenterId,
      })
      .then((r) => r.data),
  /** Doplniť jeden variant cenníka (z okna platby) a preceniť lekcie. */
  savePricingRow: (body: PricingRow & { client_reservation_ids?: number[]; center_id?: number }) =>
    api.post<{ saved: number; repriced: number }>('/pricing/row', body).then((r) => r.data),
  /** Ktorým uloženým lekciám chýba sadzba v cenníku. */
  missingPricing: (ids: number[]) =>
    api
      .post<{ missing: Array<MissingTariff & { client_reservation_id: number; start: string }> }>(
        '/pricing/missing',
        { client_reservation_ids: ids },
      )
      .then((r) => r.data.missing),
  /** Stiahnuť a spárovať platby z Fio Banka za obdobie. Bez rozsahu berie
   *  server posledný týždeň. */
  importFio: (range?: { from?: string; to?: string }) =>
    api
      .post<FioImportResult>('/settings/integrations/fio/import', range ?? {})
      .then((r) => r.data),
  /** Nahrať výpis z účtu banky (XML: camt.053 alebo OFX) a spárovať platby. */
  importStatement: (bank: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    form.append('bank', bank)
    return api
      .post<StatementImportResult>('/settings/integrations/statement/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data)
  },
}

/** Widget na online rezerváciu — nastavenie pre správcu. */
export const widget = {
  config: () => api.get<WidgetConfig>('/widget/config').then((r) => r.data),
  save: (payload: Partial<WidgetConfig>) =>
    api.put<WidgetConfig>('/widget/config', payload).then((r) => r.data),
}

export const users = {
  list: (params: Record<string, unknown>) =>
    api.get<Paginated<User>>('/users/', { params }).then((r) => r.data),
  create: (payload: Partial<User> & { password?: string }) =>
    api.post<User>('/users/', payload).then((r) => r.data),
  update: (id: number, payload: Partial<User> & { password?: string }) =>
    api.patch<User>(`/users/${id}/`, payload).then((r) => r.data),
  block: (id: number) => api.delete(`/users/${id}/`),
  unblock: (id: number) => api.post<User>(`/users/${id}/unblock/`).then((r) => r.data),
  /** Zrušiť MFA používateľovi, ktorý stratil telefón aj záložné kódy. */
  resetMfa: (id: number) => api.post<User>(`/users/${id}/reset-mfa/`).then((r) => r.data),
}

// --- reporting --------------------------------------------------------------
/**
 * Zmluvy a faktúry školy voči platforme. Iba na čítanie — mení ich operátor
 * v konzole platformy, nie škola.
 */
export const documents = {
  list: () => api.get<SchoolDocuments>('/documents').then((r) => r.data),
  contractPdf: (id: number) => pdf(`/documents/contracts/${id}/pdf`, 'zmluva.pdf'),
  invoicePdf: (id: number) => pdf(`/documents/invoices/${id}/pdf`, 'faktura.pdf'),
  /** Platba faktúry kartou - vracia adresu platobnej stránky Stripe. */
  payInvoice: (id: number) =>
    api.post<{ url: string }>(`/documents/invoices/${id}/pay`).then((r) => r.data),
}

/**
 * Fakturačné údaje školy voči platforme — na rozdiel od `documents` vyššie
 * si ich škola mení sama (obchodné meno, sídlo, IČO/DIČ, štatutár...).
 * Cenu a obdobie zmluvy nastavuje len operátor v konzole platformy.
 */
export const billing = {
  get: () => api.get<TenantBilling>('/billing').then((r) => r.data),
  update: (payload: Partial<TenantBilling>) =>
    api.patch<TenantBilling>('/billing', payload).then((r) => r.data),
  /** Faktúry po splatnosti - pás s upozornením na pozastavenie. */
  status: () =>
    api
      .get<{
        overdue: import('@/features/billing/types').PaymentNotice[]
        usage: import('@/features/billing/types').PlanUsage | null
      }>('/billing/status')
      .then((r) => r.data),
}

/** Balíček školy a prechod na iný (`contracts.upgrade`). */
export const plan = {
  get: () =>
    api.get<import('@/features/billing/types').PlanOverview>('/plan').then((r) => r.data),
  change: (key: string, note = '') =>
    api
      .post<import('@/features/billing/types').PlanChangeResult>('/plan', { plan: key, note })
      .then((r) => r.data),
  /** Platba objednávky kartou - vracia adresu platobnej stránky Stripe. */
  pay: () => api.post<{ url: string }>('/plan/pay').then((r) => r.data),
  cancelRequest: () =>
    api
      .post<import('@/features/billing/types').PlanOverview>('/plan', { cancel_request: true })
      .then((r) => r.data),
}

/**
 * Všeobecné obchodné podmienky. Prijíma ich škola, nie používateľ — záznam
 * visí na škole a platí, kým nevyjde nová verzia.
 */
export const terms = {
  get: () => api.get<TermsDocument>('/terms').then((r) => r.data),
  accept: () => api.post<TermsAcceptance>('/terms/accept').then((r) => r.data),
}

/** PDF chránené prihlásením. `<a href>` by šlo bez cookie, tak sa ťahá blob. */
function pdf(url: string, fallback: string) {
  return api.get(url, { responseType: 'blob' }).then((r) => ({
    blob: r.data as Blob,
    filename:
      /filename="([^"]+)"/.exec(r.headers['content-disposition'] ?? '')?.[1] ?? fallback,
  }))
}

export const statistics = {
  get: (interval: string, from?: string, to?: string, centers: number[] = []) =>
    api
      .get<StatisticsResponse>('/statistics', {
        params: { interval, from, to, centers: centers.length ? centers.join(',') : undefined },
      })
      .then((r) => r.data),
  /** Ten istý obsah ako zošit. Sťahuje sa cez blob, nie odkazom: endpoint
   *  vyžaduje prihlásenie a `<a href>` by šiel bez cookies aj hlavičky. */
  exportUrl: (interval: string, from?: string, to?: string, centers: number[] = []) =>
    api
      .get('/statistics/export', {
        params: { interval, from, to, centers: centers.length ? centers.join(',') : undefined },
        responseType: 'blob',
      })
      .then((r) => ({
        blob: r.data as Blob,
        filename:
          /filename="([^"]+)"/.exec(r.headers['content-disposition'] ?? '')?.[1] ??
          'statistiky.xlsx',
      })),
}

export interface RevertPlan {
  kind: ChangeKind
  /** `update` vrátenie hodnoty, `restore` obnova zmazaného záznamu,
   *  `none` nedá sa (vytvorenie, zlúčenie), `noop` záznam už existuje. */
  /** `remove` - vrátenie vytvorenia: lekcia / blok sa zmaže. */
  action: 'update' | 'restore' | 'remove' | 'none' | 'noop'
  /** Na objednávku už prišla platba - lekciu nemožno zmazať. */
  order_paid?: boolean
  /** Koho sa zmena týka (klient alebo inštruktor) - ako v prehľade zmien. */
  subject: string | null
  subject_id: number
  /** Kedy je lekcia / blok a ku ktorému inštruktorovi patrí. */
  context: { start: string | null; instructor: string | null }
  /** Kedy a kto zmenu urobil. */
  created: string | null
  user_name: string | null
  changes: Array<{ field: string; from: unknown; to: unknown }>
  /** Čísla vystavených dokladov - keď nie je prázdne, vrátenie sa odmietne. */
  receipts: string[]
  /** Vyplatené odmeny bloku - to isté pri rezerváciách inštruktorov. */
  paid_rewards: number
  /** Koľko zmien prišlo po tejto; vrátenie ich prekryje. */
  later_changes: number
  /** Pri obnove zmazanej lekcie sa poplatky počítajú nanovo z cenníka. */
  fees_rebuilt: boolean
  /** Obnovený blok nedostane späť svoje lekcie - audit nevie, ktoré to boli. */
  lessons_stay_detached: boolean
}

export const changelog = {
  revertPlan: (kind: ChangeKind, id: number) =>
    api.get<RevertPlan>(`/changelog/${kind}/${id}/revert`).then((r) => r.data),
  /** `row_id` je nový riadok, ktorý vrátenie samo zanechalo v histórii -
   *  vrátiť *ten* je „znova vykonať“ (Redo). */
  revert: (kind: ChangeKind, id: number) =>
    api
      .post<{ detail: string; row_id: number | null }>(`/changelog/${kind}/${id}/revert`)
      .then((r) => r.data),
  list: (params: { kind?: ChangeKind; q?: string; limit?: number; offset?: number }) =>
    api
      .get<{ count: number; results: ChangeEntry[] }>('/changelog', { params })
      .then((r) => r.data),
}

// --- odchádzajúca pošta -----------------------------------------------------
export interface EmailQuery {
  status?: string
  kind?: string
  client_id?: number
  instructor_id?: number
  search?: string
  ordering?: string
  page?: number
  limit?: number
}

export const emails = {
  list: (params: EmailQuery = {}) =>
    api.get<Paginated<OutgoingEmail>>('/emails/', { params }).then((r) => r.data),
  get: (id: number) =>
    api.get<OutgoingEmailDetail>(`/emails/${id}/`).then((r) => r.data),
  /** Počty podľa stavu - `{queued: 3, failed: 1}`. */
  summary: () => api.get<EmailSummary>('/emails/summary/').then((r) => r.data),
  retry: (id: number) =>
    api.post<OutgoingEmailDetail>(`/emails/${id}/retry/`).then((r) => r.data),
  cancel: (id: number) =>
    api.post<OutgoingEmailDetail>(`/emails/${id}/cancel/`).then((r) => r.data),
  /** Vygenerovať notifikácie o zmenách rezervácií a zaradiť ich do fronty. */
  generate: () =>
    api
      .post<{ created: number; queued: number; enabled: boolean }>('/emails/generate/')
      .then((r) => r.data),
}

/** Mobilný pohľad inštruktora (`/i/:token`) - bez prihlásenia. */
export const instructorPortal = {
  get: (token: string, day?: string) =>
    api
      .get<InstructorPortal>(`/public/instructor/${token}`, { params: { day } })
      .then((r) => r.data),
  mark: (token: string, lessonId: number, attendance: Attendance | null) =>
    api
      .post<{ id: number; attendance: Attendance | null }>(
        `/public/instructor/${token}/lessons/${lessonId}`,
        { attendance },
      )
      .then((r) => r.data),
}

/** Výplaty inštruktorov - sadzby inštruktora a výplatné listy (`apps.rewards.payroll`). */
export const payroll = {
  rates: (instructorId: number) =>
    api.get<InstructorRateRow[]>(`/instructors/${instructorId}/rates`).then((r) => r.data),
  /** Nová sadzba od dátumu - nevyplatené odmeny od toho dňa sa prepočítajú. */
  setRate: (instructorId: number, body: { reward_type_id: number; rate: string; valid_from: string }) =>
    api.post<{ id: number }>(`/instructors/${instructorId}/rates`, body).then((r) => r.data),
  deleteRate: (rateId: number) => api.delete(`/instructor-rates/${rateId}`),
  payslips: (instructorId?: number) =>
    api.get<Payslip[]>('/payslips', { params: { instructor_id: instructorId } }).then((r) => r.data),
  createPayslip: (body: {
    instructor_id: number
    period_from: string
    period_to: string
    /** Chýba = všetky odmeny na výplatu za obdobie. */
    reward_ids?: number[]
    taught_only?: boolean
    /** Len odmeny z blokov vybraných stredísk (`sql/040`). */
    center_ids?: number[]
    note?: string
  }) => api.post<Payslip>('/payslips', body).then((r) => r.data),
  markPaid: (id: number) => api.post<Payslip>(`/payslips/${id}/paid`).then((r) => r.data),
  cancel: (id: number) => api.post<Payslip>(`/payslips/${id}/cancel`).then((r) => r.data),
  pdfUrl: (id: number) => `/api/payslips/${id}/pdf`,
}

/** Účty (IBAN) stredísk (`sql/042`). */
export const bankAccounts = {
  list: (centerId: number) =>
    api.get<BankAccount[]>('/bank-accounts/', { params: { center_id: centerId } }).then((r) => r.data),
  create: (body: Partial<BankAccount>) => api.post<BankAccount>('/bank-accounts/', body).then((r) => r.data),
  update: (id: number, body: Partial<BankAccount>) =>
    api.patch<BankAccount>(`/bank-accounts/${id}/`, body).then((r) => r.data),
  remove: (id: number) => api.delete(`/bank-accounts/${id}/`),
}

/** Strediská školy (`sql/040`) - Nastavenia → Strediská. */
export const centers = {
  list: () => api.get<Center[]>('/centers/').then((r) => r.data),
  create: (body: Partial<Center>) => api.post<Center>('/centers/', body).then((r) => r.data),
  update: (id: number, body: Partial<Center>) => api.patch<Center>(`/centers/${id}/`, body).then((r) => r.data),
  remove: (id: number) => api.delete(`/centers/${id}/`),
}
